import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { ReleasePicker, type ReleaseChoice } from "../releases/ReleasePicker";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  BugSeverity,
  EmployeeSummary,
  PageResponse,
  Priority,
  ProjectModuleView,
  ProjectSummary,
  WorkItemDetail,
  WorkItemSummary,
  WorkItemType,
} from "../../shared/types/api";
import {
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  SEVERITY_LABELS,
  TYPE_LABELS,
  hasTestingPhase,
} from "./workItemVocabulary";
import { SEVERITY_ORDER } from "../bugs/bugVocabulary";

const TYPES: WorkItemType[] = [
  "EPIC",
  "USER_STORY",
  "TECHNICAL_TASK",
  "BUG",
  "SPIKE",
  "SUBTASK",
  "ANALYSIS",
  "DEMO_PREPARATION",
  "MIGRATION",
  "SERVER_WORK",
  "CLIENT_SERVICE",
];


/**
 * Creates a work item, or edits the fields that are freely editable.
 *
 * <p>The estimate and the dates appear only when creating. They are the <em>original</em> figures
 * (§55, §56), fixed the moment they are saved, and putting them on an edit form would invite
 * exactly the silent rewrite that makes every later variance figure meaningless. Changing them
 * afterwards has its own panels, which say plainly that the original is staying put.
 */
export function WorkItemFormDrawer({
  projectId,
  chooseProject = false,
  fixedType,
  parentId,
  parentLabel,
  item,
  open,
  onClose,
  onSaved,
}: {
  /** The project to file under. With `chooseProject` it is only the starting selection. */
  projectId?: string;
  /**
   * Offers a project picker. Set by the entry points that do not already stand in a project —
   * the shell's create button, which can be pressed from anywhere.
   */
  chooseProject?: boolean;
  /**
   * The only type this entry point creates, settled before the form opens.
   *
   * <p>"Raise a bug" opened a form offering every type and starting on Story, so the commonest
   * way to file a bug produced one of something else unless the person noticed. Where the button
   * has already said what is being created, the field states it rather than asking again.
   */
  fixedType?: WorkItemType;
  /** Pre-set when adding a child from an item's own page. */
  parentId?: string;
  parentLabel?: string;
  /** Present when editing. */
  item?: WorkItemDetail;
  open: boolean;
  onClose: () => void;
  onSaved: (created?: WorkItemDetail) => void;
}) {
  const editing = Boolean(item);

  const { can } = useAuth();
  const [chosenProjectId, setChosenProjectId] = useState(projectId ?? "");
  const [workItemType, setWorkItemType] = useState<WorkItemType>(fixedType ?? "USER_STORY");
  /* How bad the fault is, which is what somebody raising a bug actually knows. Priority — when
     it will be picked up — is a triage decision, so a bug asks this instead. */
  const [severity, setSeverity] = useState<BugSeverity>("MAJOR");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  const [priority, setPriority] = useState<Priority>("MEDIUM");
  const [storyPoints, setStoryPoints] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [testerId, setTesterId] = useState("");
  const [testingDue, setTestingDue] = useState("");
  const [estimateHours, setEstimateHours] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [labels, setLabels] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  /*
   * Only fetched when the picker is on screen. Every other entry point already stands in a
   * project, and asking the server for the whole list to fill a field nobody can see is a
   * request per drawer open for nothing.
   */
  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    enabled: open && chooseProject && !editing,
    staleTime: 5 * 60 * 1000,
  });

  /** Epics to hang new work from. Only an epic may parent the middle layer (§15). */
  const epics = useQuery({
    queryKey: ["work-items", "epics", chosenProjectId],
    queryFn: () =>
      api.get<PageResponse<WorkItemSummary>>(
        `/api/v1/work-items?projectId=${chosenProjectId}&type=EPIC&size=200`,
      ),
    enabled: open && !editing && !parentId && chosenProjectId !== "",
  });

  /* What the chosen project is built from. Empty for a product that is one thing, and the
     field is then not offered at all. */
  const projectModules = useQuery({
    queryKey: ["project-modules", chosenProjectId],
    queryFn: () =>
      api.get<ProjectModuleView[]>(`/api/v1/projects/${chosenProjectId}/modules`),
    enabled: open && chosenProjectId !== "",
  });

  const [epicId, setEpicId] = useState("");

  /*
   * §10: which release will carry this. Only offered on creation — changing it afterwards is a
   * scope change with its own endpoint and its own ledger entry, not part of a general edit.
   */
  const [release, setRelease] = useState<ReleaseChoice>({
    releaseId: null,
    scopeChangeReason: null,
    scopeChangeComment: null,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setChosenProjectId(projectId ?? "");
    setWorkItemType(
        item?.workItemType ?? fixedType ?? (parentId ? "SUBTASK" : "USER_STORY"));
    setTitle(item?.title ?? "");
    setDescription(item?.description ?? "");
    setAcceptanceCriteria(item?.acceptanceCriteria ?? "");
    setPriority(item?.priority ?? "MEDIUM");
    /* Only ever asked while creating — editing a bug's severity is the defect report's job. */
    setSeverity("MAJOR");
    setStoryPoints(item?.storyPoints != null ? String(item.storyPoints) : "");
    setAssigneeId(item?.assigneeId ?? "");
    setTesterId("");
    setTestingDue("");
    setLabels((item?.labels ?? []).join(", "));
    setEstimateHours("");
    setStartDate("");
    setDueDate("");
    setEpicId("");
    setRelease({ releaseId: null, scopeChangeReason: null, scopeChangeComment: null });
    setModuleId(item?.moduleId ?? "");
    setError(null);
  }, [open, item, parentId, projectId, fixedType]);

  const close = () => {
    setError(null);
    onClose();
  };

  const parsedLabels = labels
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (item) {
        const updated = await api.patch<WorkItemDetail>(`/api/v1/work-items/${item.id}`, {
          title,
          description: description || null,
          acceptanceCriteria: acceptanceCriteria || null,
          priority,
          storyPoints: storyPoints === "" ? null : Number(storyPoints),
          labels: parsedLabels,
          moduleId: moduleId === "" ? null : moduleId,
          /* Says "set it to none" rather than "leave it alone" — the two are the same value
             on the wire, and without the flag clearing a module would be impossible. */
          clearModule: moduleId === "" ? true : null,
          version: item.version,
        });
        onSaved(updated);
      } else {
        const created = await api.post<WorkItemDetail>("/api/v1/work-items", {
          projectId: chosenProjectId,
          workItemType,
          title,
          description: description || null,
          acceptanceCriteria: acceptanceCriteria || null,
          parentId: parentId ?? (epicId || null),
          priority,
          storyPoints: storyPoints === "" ? null : Number(storyPoints),
          assigneeId: assigneeId || null,
          originalEstimateHours: estimateHours === "" ? null : Number(estimateHours),
          startDate: startDate || null,
          dueDate: dueDate || null,
          labels: parsedLabels,
          moduleId: moduleId === "" ? null : moduleId,
          releaseId: release.releaseId,
          scopeChangeReason: release.scopeChangeReason,
          scopeChangeComment: release.scopeChangeComment,
        });
        if (workItemType === "BUG") {
          try {
            /* Severity lives on the §18 defect report rather than on the item, so it is a second
               call. The source is left at the schema's own default — whoever fills in the rest
               of the report sets it properly, and guessing it here would put a number on where
               the bug came from that nobody chose. */
            await api.put(`/api/v1/work-items/${created.id}/bug-detail`, {
              severity,
              bugSource: "DEVELOPMENT",
            });
          } catch (caught) {
            // The bug exists either way. Losing it because its severity could not be attached
            // would be worse than saying so and handing it back.
            setError(
              caught instanceof ApiError
                ? caught.message
                : "The bug was created, but its severity could not be saved.",
            );
            onSaved(created);
            return;
          }
        }
        if (hasTestingPhase(workItemType) && (testerId || testingDue)) {
          try {
            await api.put(`/api/v1/work-items/${created.id}/testing`, {
              testerId: testerId || null,
              testingDueDate: testingDue || null,
            });
          } catch (caught) {
            // The item exists either way — losing a created item because its testing plan could
            // not be saved would be far worse than reporting the failure and handing it back.
            // The drawer is deliberately left open (no close()) so the error stays on screen;
            // close() clears the error as part of dismissing, which would hide the one thing
            // this path exists to say.
            setError(
              caught instanceof ApiError
                ? caught.message
                : "The item was created, but its testing plan could not be saved.",
            );
            onSaved(created);
            return;
          }
        }
        onSaved(created);
      }
      close();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the item.");
    } finally {
      setSaving(false);
    }
  };

  // A subtask always belongs to something, so the type is only offered when there is a parent.
  const typeChoices = fixedType
      ? [fixedType]
      : parentId ? TYPES : TYPES.filter((value) => value !== "SUBTASK");

  return (
    <Drawer
      open={open}
      onClose={close}
      title={
        editing
          ? "Edit work item"
          : fixedType
            ? `New ${TYPE_LABELS[fixedType].toLowerCase()}`
            : "New work item"
      }
      subtitle={editing ? item?.itemKey : parentLabel ? `Under ${parentLabel}` : undefined}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={title.trim() === "" || (!editing && chosenProjectId === "")}
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create item"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!editing && chooseProject && (
          <Field
            label="Project"
            required
            hint={
              projectId
                ? "Taken from the screen you were on. Change it to file this somewhere else."
                : undefined
            }
          >
            <Select
              value={chosenProjectId}
              onChange={(event) => {
                setChosenProjectId(event.target.value);
                // The epic list belongs to the old project. Keeping the selection would file the
                // item under a parent from somewhere else, which the server refuses and the
                // reader cannot see.
                setEpicId("");
                setError(null);
              }}
            >
              <option value="">Choose a project</option>
              {(projects.data?.content ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectKey} · {project.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!editing && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type" required>
              <Select
                value={workItemType}
                /* Settled by the button that opened this. Shown rather than hidden, so the form
                   still says what it is about to create. */
                disabled={fixedType != null}
                onChange={(event) => {
                  setWorkItemType(event.target.value as WorkItemType);
                  // A placement refusal is about the type that was chosen. Leaving it on screen
                  // after somebody picks a different one shows them a rejection of a form they
                  // are no longer looking at — which reads as the application being wrong.
                  setError(null);
                }}
              >
                {typeChoices.map((value) => (
                  <option key={value} value={value}>
                    {TYPE_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            {/* A bug is raised with how bad it is; what it is worth doing next is settled in
                triage, so the two never share this slot. */}
            {workItemType === "BUG" ? (
              <Field label="Severity" required>
                <Select
                  value={severity}
                  onChange={(event) => setSeverity(event.target.value as BugSeverity)}
                >
                  {SEVERITY_ORDER.map((value) => (
                    <option key={value} value={value}>
                      {SEVERITY_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
            <Field label="Priority" required>
              <Select
                value={priority}
                onChange={(event) => setPriority(event.target.value as Priority)}
              >
                {PRIORITY_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            )}
          </div>
        )}

        {/* Which part of the product this belongs to. Offered only where the project has
            modules, and never required — plenty of work is cross-cutting, and forcing a choice
            fills the column with whatever was first in the list. */}
        {chosenProjectId !== "" && (projectModules.data ?? []).length > 0 && (
          <Field
            label="Module"
            hint="Which part of the product this work is in. Leave blank if it spans several or belongs to none."
          >
            <Select value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">No particular module</option>
              {(projectModules.data ?? []).map((row) => (
                <option key={row.module.id} value={row.module.id}>
                  {row.module.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!editing && chosenProjectId !== "" && can("RELEASE_VIEW") && (
          <ReleasePicker
            projectId={chosenProjectId}
            value={release}
            currentReleaseId={null}
            onChange={setRelease}
          />
        )}

        <Field label="Title" required>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Log in with a company account"
          />
        </Field>

        <Field label="Description">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={
              workItemType === "USER_STORY"
                ? "As an employee I want to log in with my company account so that I can reach the system securely."
                : "What this covers."
            }
          />
        </Field>

        <Field
          label="Acceptance criteria"
          hint="What has to be true before this is done."
        >
          <Textarea
            rows={3}
            value={acceptanceCriteria}
            onChange={(event) => setAcceptanceCriteria(event.target.value)}
          />
        </Field>

        {!editing && !parentId && workItemType !== "EPIC" && (
          <Field
            label="Epic"
            hint="Optional. §15 does not require one — this can sit directly under the project."
          >
            <Select value={epicId} onChange={(event) => setEpicId(event.target.value)}>
              <option value="">No epic</option>
              {(epics.data?.content ?? []).map((epic) => (
                <option key={epic.id} value={epic.id}>
                  {epic.itemKey} — {epic.title}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {editing && (
          <Field label="Priority" required>
            <Select
              value={priority}
              onChange={(event) => setPriority(event.target.value as Priority)}
            >
              {PRIORITY_ORDER.map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Story points"
            hint="Relative complexity. Never converted to hours (§53)."
          >
            <Input
              type="number"
              min={0}
              value={storyPoints}
              onChange={(event) => setStoryPoints(event.target.value)}
            />
          </Field>
          {!editing && (
            <Field label="Assignee" hint="One owner (§21).">
              <Select
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
              >
                <option value="">Unassigned</option>
                {(employees.data?.content ?? []).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        {!editing && hasTestingPhase(workItemType) && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tester" hint="Optional — can be set later from the item's Testing panel.">
              <Select
                value={testerId}
                onChange={(event) => setTesterId(event.target.value)}
              >
                <option value="">Unassigned</option>
                {(employees.data?.content ?? []).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Testing due">
              <Input
                type="date"
                value={testingDue}
                onChange={(event) => setTestingDue(event.target.value)}
              />
            </Field>
          </div>
        )}

        <Field label="Labels" hint="Comma separated.">
          <Input
            value={labels}
            onChange={(event) => setLabels(event.target.value)}
            placeholder="security, mobile"
          />
        </Field>

        {!editing && (
          <section className="border-t border-ink-100 pt-4">
            <h3 className="text-sm font-semibold text-ink-900">The original plan</h3>
            <p className="mb-3 mt-0.5 text-sm text-ink-500">
              Recorded once and then fixed. Everything afterwards is measured against these, which
              is why they cannot be edited later — re-estimating and moving dates have their own
              panels, and both keep these figures intact.
            </p>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Estimate (hours)">
                <Input
                  type="number"
                  min={0}
                  step="0.5"
                  value={estimateHours}
                  onChange={(event) => setEstimateHours(event.target.value)}
                />
              </Field>
              <Field label="Start">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Field>
              <Field label="Due">
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </Field>
            </div>
          </section>
        )}
      </div>
    </Drawer>
  );
}
